import {Url} from '@/utils/url';
import {STOCK_STATUS} from '@/constants/stock-status';

const getFirstVariant = (product) => {
  return product.categorizationHierarchyValues?.[0]?.children?.[0]?.options?.productCode;
};

const findFirstInStockVariant = (product, colorCode) => {
  //create an array of all in stock variants
  let inStockVariants = product.variants
    .filter((variant) => variant.globalStockStatus === STOCK_STATUS.EnumGlobalStockStatus1)
    .map((variant) => variant.code);

  if (colorCode) {
    const colorCodeVariants = product.categorizationHierarchyValues
      .find((colorHierarchy) => colorHierarchy.options?.colorSwatchName === colorCode)
      ?.children?.reduce((variantArr, child) => [...variantArr, child.options?.productCode], []);

    //find the intersection of the two arrays (if any in color array);
    if (colorCodeVariants?.length > 0)
      inStockVariants = inStockVariants.filter((variantCode) => colorCodeVariants.includes(variantCode));
  }

  //find the first variant in the sorted tree and return the product code
  let variantCode;
  product.categorizationHierarchyValues.forEach((colorHierarchy) => {
    if (variantCode) return;
    variantCode = colorHierarchy.children?.find((sizeHierarchy) => {
      const code = sizeHierarchy.options?.productCode;
      return inStockVariants.includes(code);
    })?.options?.productCode;
  });

  return variantCode;
};

const findFirstColorVariant = (product, colorCode) => {
  return product.categorizationHierarchyValues.find(
    (colorHierarchy) => colorHierarchy.options?.colorSwatchName === colorCode
  )?.children?.[0]?.options?.productCode;
};

const findDefaultVariant = (product) => {
  const params = Url.parse(window.location.search);
  if (params.colorCode) {
    return findFirstColorVariant(product, params.colorCode);
  } else {
    return getFirstVariant(product);
  }
};

const findDefaultVariantEcomm = (product) => {
  const params = Url.parse(window.location.search);
  const inStockVariant = findFirstInStockVariant(product, params.colorCode);

  if (inStockVariant) {
    return inStockVariant;
  } else if (params.colorCode) {
    return findFirstColorVariant(product, params.colorCode);
  } else {
    return getFirstVariant(product);
  }
};

const getAttributesFromVariant = (variant, product) => {
  for (let colorHierarchy of product.categorizationHierarchyValues) {
    const colorAttributeCode = colorHierarchy.code;
    const colorCode = colorHierarchy.options.colorSwatchName;
    for (let sizeHierarchy of colorHierarchy.children) {
      const sizeAttributeCode = sizeHierarchy.code;
      const sizeCode = sizeHierarchy.options.displayName;
      if (sizeHierarchy.options.productCode === variant)
        return {colorAttributeCode, colorCode, sizeAttributeCode, sizeCode};
    }
  }
  //if completed loop above, variant wasn't found - return default
  return {
    colorAttributeCode: product.categorizationHierarchyMetadata?.[0]?.code ?? '',
    colorCode: product.categorizationHierarchyValues?.[0]?.options?.colorSwatchName ?? '',
    sizeAttributeCode: product.categorizationHierarchyMetadata?.[1]?.code ?? '',
    sizeCode: product.categorizationHierarchyMetadata?.[0]?.children?.[0]?.options?.displayName ?? '',
  };
};

const InitProductAttributes = {
  created() {
    this.initProductAttributes();
  },

  methods: {
    /**
     * Initialize selected attributes if variant ID has been passed in URL string.
     * @return {boolean}
     */
    selectVariantFromQueryString() {
      const queryProductId = window.location.href.match(/\/p\/([a-zA-Z0-9-]+)\//i)?.[1] ?? '';
      if (!queryProductId || !this.product.variants || this.product.code === queryProductId) {
        return false;
      }

      let setFromVariant = false;
      const setInitProductAttributes = (items, parents = []) => {
        if (!items || !items.length) {
          return;
        }

        for (let item of items) {
          const productCode = item.options?.productCode;
          if (productCode && productCode === queryProductId) {
            setFromVariant = true;
            [...parents, item].forEach((item) => {
              this.$set(
                this.initAttributeSelection,
                item.code,
                item.options.colorSwatchName || item.options.displayName
              );
            });
          }

          setInitProductAttributes(item.children, [...parents, item]);
        }
      };

      setInitProductAttributes(this.product.categorizationHierarchyValues);
      return setFromVariant;
    },

    /**
     * Initialize selected attributes.
     */
    initProductAttributes() {
      if (!this.product.variants || !this.product.variants.length || this.selectVariantFromQueryString()) {
        return;
      }

      //sort the sizing hierarchy tree
      this.product.categorizationHierarchyValues = this.product.categorizationHierarchyValues.map((colorHierarchy) => {
        return {
          ...colorHierarchy,
          children: colorHierarchy.children?.sort((a, b) => a.weight - b.weight),
        };
      });

      //find variant
      const selectedVariant =
        this.options.eCommAvailable || this.b2b
          ? findDefaultVariantEcomm(this.product)
          : findDefaultVariant(this.product);

      // pull attribute values from selected variant
      const {colorAttributeCode, colorCode, sizeAttributeCode, sizeCode} = getAttributesFromVariant(
        selectedVariant,
        this.product
      );

      this.$set(this.initAttributeSelection, colorAttributeCode, colorCode);
      this.$set(this.initAttributeSelection, sizeAttributeCode, sizeCode);
      this.initVariant = selectedVariant;
    },
  },

  data() {
    return {
      initAttributeSelection: {},
      initVariant: '',
    };
  },
};

export {InitProductAttributes};
